import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserAccount } from '@/types/api';
import authService from '@/services/auth.service';
import { syncGeoServerTokenCookie } from '@/utils/geoserverAccess';
import { resetLayerSession } from '@/hooks/useLayers';
import { chooseCompanyAgain, CompanyChoice, completeSignIn, signOut, startSignIn, switchCompany, usesUms } from '@/auth/umsSession';

interface AuthContextType {
  user: UserAccount | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasAuthority: (authority: string) => boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  /** Sign-in goes through UMS (auth-config.json); the username/password form is not shown then. */
  ums: boolean;
  /** UMS: go to the identity provider. */
  signInWithUms: () => Promise<void>;
  /** UMS: the person is in several companies and picks one. */
  companies: CompanyChoice[] | null;
  chooseCompany: (tenant: string) => Promise<void>;
  /** UMS: back to the list of the person's companies (the platform among them, for our staff). */
  changeCompany: () => Promise<void>;
  /** UMS: why the last sign-in was refused, if it was. */
  signInError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ums, setUms] = useState(false);
  const [companies, setCompanies] = useState<CompanyChoice[] | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const afterUmsSignIn = async (result: Awaited<ReturnType<typeof completeSignIn>>) => {
    if (result.state === 'signed-in') {
      setCompanies(null);
      setSignInError(null);
      setUser(await authService.getAccount());
    } else if (result.state === 'choose-company') {
      setCompanies(result.companies);
    } else if (result.state === 'refused') {
      setSignInError(result.reason);
    }
  };

  useEffect(() => {
    // Load user account on mount
    const loadAccount = async () => {
      try {
        if (await usesUms()) {
          setUms(true);
          // Back from the identity provider, or a reload: a fresh GeoNexora token, silently.
          await afterUmsSignIn(await completeSignIn());
          return;
        }
        const token = localStorage.getItem('auth_token');
        // Sessions from before the GeoServer proxy have no cookie yet.
        syncGeoServerTokenCookie();
        if (token) {
          const account = await authService.getAccount();
          setUser(account);
        }
      } catch (error) {
        console.error('Failed to load account:', error);
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    loadAccount();
  }, []);

  const signInWithUms = async () => {
    setSignInError(null);
    await startSignIn();
  };

  const chooseCompany = async (tenant: string) => {
    setIsLoading(true);
    // Another company: its own layers and nothing read for the previous one.
    resetLayerSession();
    queryClient.clear();
    try {
      await afterUmsSignIn(await switchCompany(tenant));
    } finally {
      setIsLoading(false);
    }
  };

  const changeCompany = async () => {
    setIsLoading(true);
    resetLayerSession();
    queryClient.clear();
    // Signed out of the company only: the identity provider's session stays, the list comes back.
    setUser(null);
    try {
      await afterUmsSignIn(await chooseCompanyAgain());
    } finally {
      setIsLoading(false);
    }
  };

  const hasAuthority = (authority: string): boolean => {
    if (!user) return false;
    return user.authorities.includes(authority);
  };

  const login = async (username: string, password: string) => {
    try {
      await authService.login(username, password);
      const account = await authService.getAccount();
      setUser(account);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    resetLayerSession();
    // Whatever was read for this person (companies, people, layers) is not the next person's.
    queryClient.clear();
    setUser(null);
    if (ums) {
      void signOut();
    }
  };

  const refreshUser = async () => {
    try {
      const account = await authService.getAccount();
      setUser(account);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        hasAuthority,
        login,
        logout,
        refreshUser,
        ums,
        signInWithUms,
        companies,
        chooseCompany,
        changeCompany,
        signInError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
